import { Fragment, useCallback, useEffect, useState } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { Controller, SubmitHandler, useForm } from '@softland/forms';
import { yupResolver } from '@softland/forms/resolver';
import DoneIcon from '@mui/icons-material/Done';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { twoFaValidation } from './validation';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { useLocation, useNavigate } from 'react-router-dom';
import { CredentialsResponseModel, otp, UserType } from 'modules/auth/api/auth.api';

interface TwoFaSetUpFormModel {
  setupKey: string;
  otp: string;
  trustDevice: boolean;
}

export const TwoFaSetUp = () => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [returnUrl, setReturnUrl] = useState<string | undefined>(undefined);
  const [twoFaData, setTwoFaData] = useState<CredentialsResponseModel | null>(null);
  const [open, setOpen] = useState(false);

  const onCopy = useCallback(() => {
    setOpen(true);
    setTimeout(() => setOpen(false), 2000);
  }, []);

  const { control, handleSubmit, reset } = useForm<TwoFaSetUpFormModel>({
    defaultValues: {
      otp: '',
      setupKey: '',
      trustDevice: false,
    },
    resolver: yupResolver(twoFaValidation),
  });

  useEffect(() => {
    if (location.state?.twoFaState?.twoFaInfo) {
      setTwoFaData(location.state.twoFaState);
      setReturnUrl(location.state.returnUrl);
      reset({
        setupKey: location.state.twoFaState.twoFaInfo.key,
        otp: '',
        trustDevice: false,
      });
    } else {
      navigate('/error', {
        state: {
          message: 'page not found',
        },
      });
    }
  }, [location.state, navigate, reset, returnUrl]);

  const onSubmit: SubmitHandler<TwoFaSetUpFormModel> = async data => {
    setLoading(true);
    setErrorMessage('');

    try {
      const response = await otp({
        token: twoFaData!.token,
        otp: data.otp,
        trustDevice: data.trustDevice,
      });

      if (returnUrl) {
        window.location.replace(returnUrl);
      } else {
        window.location.replace(response.data);
      }
    } catch (error: any) {
      setErrorMessage('Enter a valid code');
    }

    setLoading(false);
  };

  return (
    <Stack
      padding={3}
      gap={3}
      alignItems="center"
      justifyContent="center"
      sx={{
        backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.09)' : 'none',
      }}
    >
      {twoFaData ? (
        <Fragment>
          <Box>
            <Typography variant="h5" className="qa-two-fa-title" color="text.primary">
              Set Up Two-Factor Authentication
            </Typography>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ color: theme.palette.text.secondary, marginTop: '7px', marginBottom: '15px' }}
              className="qa-welcome-message"
            >
              Scan QR with Google Authenticator app or use the code below.
            </Typography>
          </Box>
          <Box margin="auto">
            <img src={twoFaData.twoFaInfo?.qrInBase64} alt="QR" />
          </Box>
          <Box width="100%">
            <form onSubmit={handleSubmit(onSubmit)}>
              <Stack gap={2}>
                <Controller
                  name="setupKey"
                  control={control}
                  render={({ field: { onBlur, value }, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      size="medium"
                      id="qa-login-email"
                      label="Setup Key"
                      variant="outlined"
                      onBlur={onBlur}
                      value={value}
                      error={!!error}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <CopyToClipboard onCopy={() => onCopy()} text={value}>
                              <Tooltip open={open} title={value} arrow>
                                <IconButton size="small">
                                  {open ? (
                                    <DoneIcon color="success" />
                                  ) : (
                                    <ContentCopyIcon fontSize="inherit" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            </CopyToClipboard>
                          </InputAdornment>
                        ),
                        sx: {
                          '& input': {
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          },
                        },
                      }}
                    />
                  )}
                />

                <Controller
                  name="otp"
                  control={control}
                  render={({ field: { onBlur, value, onChange }, fieldState: { error } }) => (
                    <TextField
                      fullWidth
                      size="medium"
                      id="qa-login-email"
                      label="Verification Code"
                      variant="outlined"
                      onBlur={onBlur}
                      value={value}
                      onChange={e => onChange(e.target.value.trim())}
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
                {twoFaData.userType === UserType.BACKOFFICE && (
                  <Controller
                    name="trustDevice"
                    control={control}
                    render={({ field: { value, onChange } }) => (
                      <FormControlLabel
                        control={<Checkbox size="small" checked={value} onChange={onChange} />}
                        label="Trust this device"
                      />
                    )}
                  />
                )}
                <Box>
                  <LoadingButton
                    id="qa-login-button"
                    loading={loading}
                    size="large"
                    variant="contained"
                    type="submit"
                    fullWidth
                  >
                    Confirm
                  </LoadingButton>
                  {errorMessage && (
                    <Typography
                      variant="caption"
                      sx={{ color: 'error.main', padding: '10px' }}
                      className="qa-error-message"
                    >
                      {errorMessage}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </form>
          </Box>
        </Fragment>
      ) : (
        <CircularProgress />
      )}
    </Stack>
  );
};
